/**
 * Context Menu State Store
 *
 * Manages global context menu visibility and position state.
 */

import { create } from 'zustand';
import type { ContextMenuEntry } from '../config/contextMenuConfig';

export interface ContextMenuState {
    visible: boolean;
    position: { x: number; y: number };
    targetElementId: string | null;
    targetElementIds: string[];
    /**
     * Caller-supplied menu entries (sketch toolbar settings menu, ...). When
     * set, the shared ContextMenu renders THESE instead of the selection
     * config tree; hide() clears them again.
     */
    customItems: ContextMenuEntry[] | null;

    show: (x: number, y: number, targetId?: string, targetIds?: string[], items?: ContextMenuEntry[]) => void;
    /** Open the shared menu with explicit entries (no selection semantics). */
    showCustom: (x: number, y: number, items: ContextMenuEntry[]) => void;
    hide: () => void;
}

export const useContextMenuStore = create<ContextMenuState>((set, get) => ({
    visible: false,
    position: { x: 0, y: 0 },
    targetElementId: null,
    targetElementIds: [],
    customItems: null,

    show: (x, y, targetId = undefined, targetIds = [], items) => {
        // Adjust position if near screen edges
        const menuWidth = 240; // Approximate menu width
        const menuHeight = 400; // Approximate max menu height

        let adjustedX = x;
        let adjustedY = y;

        if (typeof window !== 'undefined') {
            if (x + menuWidth > window.innerWidth) {
                adjustedX = window.innerWidth - menuWidth - 8;
            }
            if (y + menuHeight > window.innerHeight) {
                adjustedY = Math.max(8, window.innerHeight - menuHeight - 8);
            }
        }

        set({
            visible: true,
            position: { x: adjustedX, y: adjustedY },
            targetElementId: targetId,
            targetElementIds: targetIds,
            customItems: items ?? null,
        });
    },

    showCustom: (x, y, items) => {
        get().show(x, y, undefined, [], items);
    },

    hide: () => set({
        visible: false,
        targetElementId: null,
        targetElementIds: [],
        customItems: null,
    }),
}));
